import {
  Box,
  Button,
  Divider,
  MenuItem,
  Popover,
  Slider,
  styled,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';

import {
  DEFAULT_WIND_THEME_ID,
  DEFAULT_WIND_THEME_OPACITY,
  DEFAULT_WIND_THEME_THRESHOLD,
  SYSTEM_THEME_IDS,
  useWindTheme,
  WIND_COLOR_THEMES,
} from '../model';

const PREFIX = 'TSMED-WindThemeToggler';
const togglerClasses = {
  sectionLabel: `${PREFIX}-sectionLabel`,
  itemName: `${PREFIX}-itemName`,
  divider: `${PREFIX}-divider`,
  sliderLabel: `${PREFIX}-sliderLabel`,
  sliderBox: `${PREFIX}-sliderBox`,
  resetBox: `${PREFIX}-resetBox`,
};

const TriggerButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  zIndex: 1000,
  left: 240,
  backgroundColor: theme.palette.mapBlue[700],
  border: `2px solid ${theme.palette.mapBlue[600]}`,
  borderRadius: theme.spacing(1.5),
  boxShadow: theme.shadows[3],
  padding: theme.spacing(0, 1),
  minWidth: theme.spacing(5),
  color: theme.palette.common.white,
  height: theme.spacing(5),
  overflow: 'hidden',
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'none',
  gap: theme.spacing(0.5),
  '&:hover': { backgroundColor: theme.palette.mapBlue[800] },
}));

const Swatch = styled(Box)<{ colors: readonly string[] }>(({ colors }) => ({
  width: 16,
  height: 16,
  borderRadius: 4,
  flexShrink: 0,
  background: `linear-gradient(to right, ${colors.join(', ')})`,
}));

const PanelRoot = styled(Box)(({ theme: { spacing, palette } }) => ({
  padding: spacing(1),
  minWidth: 200,
  backgroundColor: palette.mapBlue[700],
  color: palette.common.white,

  [`.${togglerClasses.sectionLabel}`]: {
    paddingInline: spacing(1),
    opacity: 0.7,
  },

  [`.${togglerClasses.itemName}`]: {
    flex: 1,
  },

  [`.${togglerClasses.divider}`]: {
    marginBlock: spacing(0.5),
    borderColor: 'rgba(255,255,255,0.15)',
  },

  [`.${togglerClasses.sliderLabel}`]: {
    opacity: 0.7,
  },

  [`.${togglerClasses.sliderBox}`]: {
    paddingInline: spacing(1),
    paddingTop: spacing(0.5),
  },

  [`.${togglerClasses.resetBox}`]: {
    paddingInline: spacing(1),
    paddingTop: spacing(0.5),
    paddingBottom: spacing(0.5),
  },
}));

const StyledPopover = styled(Popover)(({ theme: { spacing } }) => ({
  '& .MuiPaper-root': {
    marginTop: spacing(0.5),
    borderRadius: spacing(1.5),
    overflow: 'hidden',
  },
}));

const ThemeItem = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontSize: '12px',
  borderRadius: theme.spacing(0.5),
  color: theme.palette.common.white,
  '&:hover': { backgroundColor: theme.palette.mapBlue[600] },
  '&.Mui-selected': {
    backgroundColor: theme.palette.mapBlue[500],
    '&:hover': { backgroundColor: theme.palette.mapBlue[400] },
  },
}));

const StyledSlider = styled(Slider)(({ theme }) => ({
  color: theme.palette.common.white,
  '& .MuiSlider-thumb': { width: 14, height: 14 },
}));

const ResetButton = styled(Button)(({ theme }) => ({
  borderColor: 'rgba(255,255,255,0.3)',
  color: theme.palette.common.white,
  fontSize: '11px',
  textTransform: 'none',
  '&:hover': {
    borderColor: 'rgba(255,255,255,0.55)',
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  '&.Mui-disabled': {
    borderColor: 'rgba(255,255,255,0.18)',
    color: 'rgba(255,255,255,0.45)',
  },
}));

export const WindThemeToggler = () => {
  const {
    theme,
    themeId,
    setThemeId,
    opacity,
    threshold,
    setOpacity,
    setThreshold,
  } = useWindTheme();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const isDefaultState =
    themeId === DEFAULT_WIND_THEME_ID &&
    Math.abs(opacity - DEFAULT_WIND_THEME_OPACITY) < 0.001 &&
    Math.abs(threshold - DEFAULT_WIND_THEME_THRESHOLD) < 0.001;

  const handleResetDefaults = () => {
    setThemeId(DEFAULT_WIND_THEME_ID);
    setOpacity(DEFAULT_WIND_THEME_OPACITY);
    setThreshold(DEFAULT_WIND_THEME_THRESHOLD);
  };

  return (
    <>
      <TriggerButton
        ref={anchorRef}
        className={`${PREFIX}-root`}
        onClick={() => setOpen(prev => !prev)}
        title={`Theme: ${theme.name}`}
      >
        <Swatch colors={theme.gradient?.map(([, c]) => c) ?? []} />
      </TriggerButton>

      <StyledPopover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <PanelRoot>
          <Typography variant="caption" className={togglerClasses.sectionLabel}>
            System Themes
          </Typography>
          {SYSTEM_THEME_IDS.map(id => {
            const t = WIND_COLOR_THEMES[id];
            return (
              <ThemeItem
                key={id}
                selected={id === themeId}
                onClick={() => {
                  setThemeId(id);
                  setOpen(false);
                }}
              >
                <Swatch colors={t.gradient?.map(([, c]) => c) ?? []} />
                <Box className={togglerClasses.itemName}>{t.name}</Box>
              </ThemeItem>
            );
          })}

          <Divider className={togglerClasses.divider} />

          <Box className={togglerClasses.sliderBox}>
            <Typography
              variant="caption"
              className={togglerClasses.sliderLabel}
            >
              Opacity: {Math.round(opacity * 100)}%
            </Typography>
            <StyledSlider
              value={opacity}
              min={0}
              max={1}
              step={0.01}
              onChange={(_, v) => setOpacity(v as number)}
              size="small"
            />
          </Box>

          <Box className={togglerClasses.sliderBox}>
            <Typography
              variant="caption"
              className={togglerClasses.sliderLabel}
            >
              Threshold: {Math.round(threshold * 100)}%
            </Typography>
            <StyledSlider
              value={threshold}
              min={0}
              max={1}
              step={0.01}
              onChange={(_, v) => setThreshold(v as number)}
              size="small"
            />
          </Box>

          <Box className={togglerClasses.resetBox}>
            <ResetButton
              fullWidth
              variant="outlined"
              size="small"
              disabled={isDefaultState}
              onClick={handleResetDefaults}
            >
              Reset to defaults
            </ResetButton>
          </Box>
        </PanelRoot>
      </StyledPopover>
    </>
  );
};
